import React, { useMemo } from 'react';

import { Box, Card, CardContent, Radio, Typography } from '@mui/material';

import Button from 'components/Button';
import Text from 'components/Text';
import { useThemeContext } from 'global/ThemeContext/ThemeContext';

interface Event {
  id: number;
  name: string;
  description: string;
  init_datetime: string;
  end_datetime: string;
  is_online: boolean;
  location?: string;
  total_participants?: number;
}

interface GroupedEvent {
  id: number; // ID of the most recent occurrence
  name: string;
  description: string;
  init_datetime: string; // Date of the most recent occurrence
  end_datetime: string;
  is_online: boolean;
  location?: string;
  total_participants?: number;
  allOccurrences: Event[]; // All occurrences for this event
}

interface EventSelectionStepProps {
  events: Event[];
  selectedEventId: number | null;
  onSelectEvent: (eventId: number) => void;
  onSubscribe: () => void;
  loading: boolean;
}

export const EventSelectionStep: React.FC<EventSelectionStepProps> = ({
  events,
  selectedEventId,
  onSelectEvent,
  onSubscribe,
  loading,
}) => {
  const theme = useThemeContext();

  // Group events by name and get the most recent occurrence for each
  const groupedEvents = useMemo(() => {
    const eventMap = new Map<string, Event[]>();

    // Group events by name
    events.forEach(event => {
      if (!eventMap.has(event.name)) {
        eventMap.set(event.name, []);
      }
      eventMap.get(event.name)!.push(event);
    });

    // Convert to GroupedEvent array, keeping only the most recent occurrence
    return Array.from(eventMap.values()).map(eventGroup => {
      // Sort by init_datetime descending to get the most recent first
      const sortedEvents = eventGroup.sort(
        (a, b) => new Date(a.init_datetime).getTime() - new Date(b.init_datetime).getTime()
      );

      const mostRecent = sortedEvents[0];

      return {
        id: mostRecent.id,
        name: mostRecent.name,
        description: mostRecent.description,
        init_datetime: mostRecent.init_datetime,
        end_datetime: mostRecent.end_datetime,
        is_online: mostRecent.is_online,
        location: mostRecent.location,
        total_participants: mostRecent.total_participants,
        allOccurrences: sortedEvents,
      } as GroupedEvent;
    });
  }, [events]);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Box sx={{ width: '100%', maxWidth: 600, margin: '0 auto', padding: '20px 0' }}>
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Text size="medium" color="textPrimary">
          Selecione o evento para se inscrever
        </Text>
      </Box>

      {groupedEvents.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Text color="textSecondary">Nenhum evento disponível no momento.</Text>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
          {groupedEvents.map(event => (
            <Card
              key={event.id}
              sx={{
                backgroundColor: theme.background.secondary,
                border: selectedEventId === event.id ? `2px solid ${theme.primary}` : 'none',
                transition: 'all 0.2s',
                cursor: 'pointer',
                '&:hover': {
                  boxShadow: 3,
                },
              }}
              onClick={() => onSelectEvent(event.id)}
            >
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Radio checked={selectedEventId === event.id} sx={{ mr: 1 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="h6"
                      sx={{ color: theme.text.primary, fontWeight: 'bold', mb: 1 }}
                    >
                      {event.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: theme.text.secondary, mb: 1 }}>
                      {event.description}
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                        📅 {formatDate(event.init_datetime)}
                        {event.allOccurrences.length > 1 && (
                          <span style={{ marginLeft: '8px', fontStyle: 'italic' }}>
                            (Próxima ocorrência)
                          </span>
                        )}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                        {event.is_online ? '🌐 Online' : '📍 Presencial'}
                        {event.location && !event.is_online && ` - ${event.location}`}
                      </Typography>
                      {event.total_participants !== undefined && (
                        <Typography variant="caption" sx={{ color: theme.text.secondary }}>
                          👥 {event.total_participants} participantes
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Button
          type="button"
          variant="primary"
          fullWidth
          onClick={onSubscribe}
          disabled={!selectedEventId || loading}
          loading={loading}
        >
          {loading ? 'Inscrevendo...' : 'Inscrever-se no Evento'}
        </Button>
      </Box>
    </Box>
  );
};
